import { useIntl } from "react-intl";
import { SourceBadge } from "~/components/SourceBadge";

/**
 * Source reference from RAG system
 */
export interface SourceReference {
  organizationId: string;
  projectId: string;
  documentId: string;
  pageId: string;
  filename: string;
  pagePreviewUrl: string;
  text?: string | null;
  isKnowledgeBase?: boolean | null;
  batesNumber?: string | null;
}

/**
 * Extended ReportSection type that includes sources from RAG.
 * This type is used during the transition period before the generated types are updated.
 */
export interface ReportSectionWithSources {
  content?: string | null;
  id?: string;
  order?: number;
  status?: string;
  title?: string;
  sources?: SourceReference[] | null;
}

interface ReportSectionSourcesProps {
  sources: SourceReference[];
  onSourceClick?: (source: SourceReference) => void;
}

/**
 * Component to display source references for a report section.
 * Similar to how sources are displayed in chat messages.
 */
export const ReportSectionSources = ({
  sources,
  onSourceClick,
}: ReportSectionSourcesProps) => {
  const { formatMessage } = useIntl();

  // Deduplicate sources by pageId + documentId
  const uniqueSources = sources.filter(
    (source, index, self) =>
      index ===
      self.findIndex(
        (s) => s.pageId === source.pageId && s.documentId === source.documentId
      )
  );

  if (!uniqueSources || uniqueSources.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex items-center gap-1 flex-wrap">
      <span className="text-xs text-muted-foreground">
        {formatMessage({ id: "chat.sources" })}:
      </span>
      {uniqueSources.map((source, index) => (
        <SourceBadge
          key={`${source.documentId}-${source.pageId}`}
          onClick={() => onSourceClick?.(source)}
          title={`${source.filename} - Page ${source.pageId}`}
        >
          {index + 1}
        </SourceBadge>
      ))}
    </div>
  );
};
